import "reflect-metadata"
import { inter, roboto, robotoMono } from "core/utils/fonts"
import { appWithTranslation, useTranslation } from "next-i18next"
import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react"
import { RecoilRoot } from "recoil"
import nextI18nConfig from "../next-i18next.config"

import "@fortawesome/fontawesome-svg-core/styles.css"
import Head from "next/head"
import AuthenticationProvider from "../components/authentication"
import "../styles/globals.css"
import "../styles/reactGrid.css"
import { disableDuplicateWarning } from "../utils/recoil"

global.console = disableDuplicateWarning(global.console)

import { Header } from "@/modules/navigation/containers/Header"
import { Loader } from "../components/spinners/loader"

import { Worker } from "@react-pdf-viewer/core"
import { ErrorBoundary } from "@sentry/nextjs"
import packageJson from "../package.json"
import Error500Page from "./500"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ThemeProvider } from "context/theme-context"
import { SWRConfig } from "swr"
import { NotificationsProvider } from "../context/notifications"
import PusherProvider from "../context/pusher-context"
import {
  NavigationProvider,
  SideBar,
} from "@/modules/navigation/containers/SideBar"
import { Snackbar } from "@/core/components/Snackbar"
import { ToastContainer } from "@/core/components/ToastContainer"
import dayjs from "dayjs"
import RouteGuard from "@/modules/navigation/guards/RouteGuard"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import Script from "next/script"
import { initZodI18n } from "@/core/utils/initZodI18n"
import { getAppPageKey } from "@/core/utils/getAppPageKey"
import { isSharedProposalRoute } from "@/features/shared_proposal/sharedRoutes"
import { ScrollbarWidthCalculator } from "@/core/components/ScrollbarWidthCalculator"
import { AppProps } from "next/app"
import { ICON_FONT } from "@/core/components/icon/Icon"
import { ScrollArea } from "@/core/components/ScrollArea"
import { HeroBannerProvider } from "@/core/components/HeroBanner/context/HeroBannerContext"
import { HeroBannerContainer } from "@/core/components/HeroBanner/HeroBannerContainer"
import { FreshdeskScript } from "@/core/components/FreshdeskScript"

dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

export const queryClient = new QueryClient()

initZodI18n()

function MyApp({ Component, router, pageProps }: AppProps) {
  const { i18n } = useTranslation()
  dayjs.locale(i18n.language)
  const headerRef = useRef<HTMLElement>(null)
  const mainViewportRef = useRef<HTMLDivElement>(null)
  const mainScrolledRef = useRef(false)

  const setMainScrolledAppearance = (scrolled: boolean) => {
    if (mainScrolledRef.current === scrolled) return
    mainScrolledRef.current = scrolled
    const header = headerRef.current
    if (!header) return
    header.classList.toggle("border-b", scrolled)
    header.classList.toggle("border-outline-variant/50", scrolled)
  }

  const handleMainScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    setMainScrolledAppearance(e.currentTarget.scrollTop > 0)
  }

  useEffect(() => {
    const handlePopState = () => {
      const url =
        window.location.pathname + window.location.search + window.location.hash
      if (url !== router.asPath) {
        router.replace(url, undefined, { shallow: false })
      }
    }
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [router])

  useEffect(() => {
    if (typeof window === "undefined") return
    const stored = localStorage.getItem("language")

    if (stored && stored !== i18n.language) {
      i18n.changeLanguage(stored)
    }
  }, [i18n])

  useLayoutEffect(() => {
    if (typeof window === "undefined") return
    if (document.fonts.check(ICON_FONT)) return
    void document.fonts.load(ICON_FONT)
  }, [])

  useLayoutEffect(() => {
    const viewport = mainViewportRef.current
    if (viewport) {
      viewport.scrollTop = 0
    }
    window.scrollTo(0, 0)
    setMainScrolledAppearance(false)
  }, [router.asPath])

  const pdfjsWorkerVersion: string = useMemo(
    () => packageJson.dependencies["pdfjs-dist"],
    [],
  )
  const isLoginPage = router.pathname === "/login"
  const isRedirectPage =
    router.pathname === "/r" || router.pathname === "/r/[code]"
  // Public, no-login shared-proposal routes render with no app chrome
  // (no SideBar/Header/RouteGuard) — same bare path as login/redirect.
  const isSharePage = isSharedProposalRoute(router.pathname)

  const page =
    isLoginPage || isRedirectPage || isSharePage ? (
      <Component {...pageProps} key={getAppPageKey(router.asPath)} />
    ) : (
      <PusherProvider>
        <NotificationsProvider />
        <HeroBannerProvider>
          <div
            className={
              // max-w-screen bg-surface p-0
              `${roboto.variable} ${inter.variable} ${robotoMono.variable} font-inter`
              //className={`${roboto.variable} ${inter.variable} ${robotoMono.variable}`}
            }
          >
            <div className="flex h-screen min-h-0 overflow-hidden">
              <NavigationProvider>
                {/* <SideBar /> */}
                <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
                  <HeroBannerContainer />
                  <Header ref={headerRef} />
                  <ScrollArea
                    scrollbars="both"
                    className="min-h-0 w-full flex-1"
                    barClassName="z-50"
                    viewportRef={mainViewportRef}
                    onViewportScroll={handleMainScroll}
                  >
                    <main className="mx-auto min-h-full w-full min-w-[1268px] max-w-[1452px] px-10 pb-10 pt-6">
                      <Worker
                        workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsWorkerVersion}/build/pdf.worker.min.js`}
                      >
                        <RouteGuard router={router}>
                          <Component
                            {...pageProps}
                            key={getAppPageKey(router.asPath)}
                          />
                        </RouteGuard>
                      </Worker>
                      <div id="modal-root" />
                    </main>
                  </ScrollArea>
                </div>
              </NavigationProvider>
            </div>
          </div>
        </HeroBannerProvider>
      </PusherProvider>
    )

  return (
    <ErrorBoundary fallback={<Error500Page />}>
      <QueryClientProvider client={queryClient}>
        <RecoilRoot>
          <React.Suspense fallback={<Loader />}>
            <SWRConfig
              value={{
                revalidateOnFocus: false,
                revalidateOnReconnect: false,
              }}
            >
              <ThemeProvider>
                <Head>
                  <meta name="robots" content="noindex, nofollow" />
                  <title>Flextribe - Workforce Management System</title>
                  <meta
                    name="viewport"
                    content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
                  />
                </Head>
                <AuthenticationProvider>
                  <FreshdeskScript />
                  {page}
                </AuthenticationProvider>
              </ThemeProvider>
            </SWRConfig>
          </React.Suspense>
        </RecoilRoot>
        <Snackbar />
        <ToastContainer />
      </QueryClientProvider>
      <ScrollbarWidthCalculator />
    </ErrorBoundary>
  )
}

export default appWithTranslation(MyApp, nextI18nConfig)
